import React from 'react';
import { styled } from '@linaria/react';
import { motion } from 'framer-motion';

import ClientOnlySnippet from '@/components/CodeSnippet/ClientOnlySnippet';
import { DemoToggleButtonGroupControl } from '@/components/Demo';

import GridDemo from './GridDemo';
import Item from './Item';
import GridAreasStructure from './GridAreasStructure';
import { BaseCodeWrapper } from './Grid.shared';

function GridAreas() {
  return (
    <GridDemo
      hidePerspectiveControl
      defaultPerspectiveEnabled={false}
      initialValues={{
        gridArea: 'sidebar',
      }}
      customGrid={{
        gridTemplateColumns: '1fr',
      }}
      controls={[
        <ControlWrapper key="gridArea">
          <DemoToggleButtonGroupControl
            inline
            inlineMinWidth={500}
            id="gridArea"
            label="Grid Area"
            options={['sidebar', 'header', 'main']}
          />
        </ControlWrapper>,
      ]}
    >
      {({ gridArea, isPerspectiveEnabled }) => {
        const cssCode = `\
.parent {
  display: grid;
  grid-template-columns: 2fr 5fr;
  grid-template-rows: 50px 1fr;
  grid-template-areas:
    'sidebar header'
    'sidebar main';
}

.child {
  grid-area: ${gridArea};
}`;

        return (
          <Row>
            <CodeWrapper>
              <ClientOnlySnippet
                omitCopyButton
                lockedToColorMode="dark"
                code={cssCode}
                lang="css"
              />
            </CodeWrapper>
            <Wrapper>
              <BackLayer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translate(10%, 10%) skewY(18deg) scaleX(0.85)'
                    : 'translate(0%, 0%) skewY(0deg) scaleX(1)',
                }}
              >
                <GridAreasStructure />
              </BackLayer>
              <Layer
                style={{
                  transform: isPerspectiveEnabled
                    ? 'translate(-10%, 10%) skewY(18deg) scaleX(0.85)'
                    : 'translate(0%, 0%) skewY(0deg) scaleX(1)',
                }}
              >
                <ChildWrapper>
                  <Item
                    layout={true}
                    primary
                    style={{
                      gridArea,
                      opacity: isPerspectiveEnabled ? 0.8 : 1,
                    }}
                  />
                </ChildWrapper>
              </Layer>
            </Wrapper>
          </Row>
        );
      }}
    </GridDemo>
  );
}

const ControlWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  margin-top: 16px;
  margin-bottom: 16px;

  @media (max-width: 45rem) {
    flex-direction: column-reverse;
    align-items: center;
    gap: 48px;
  }

  @media (max-width: 30rem) {
    margin-bottom: -16px;
  }
`;

const Wrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 350px;
  height: 18.75rem;

  @media (max-width: 45rem) {
    /* Align with the code snippet on mobile */
    max-width: 380px;
  }
`;

const Layer = styled(motion.div)`
  transition: transform 500ms;
  will-change: transform;
  height: 100%;
`;

const BackLayer = styled(Layer)`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
`;

const ChildWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-columns: 2fr 5fr;
  grid-template-rows: 50px 1fr;
  grid-template-areas:
    'sidebar header'
    'sidebar main';
  gap: 6px;
  padding: 2px;
`;

const CodeWrapper = styled(BaseCodeWrapper)`
  min-height: 190px;
  min-width: 380px;

  @media (max-width: 30rem) {
    width: calc(100% + 32px);
    max-width: calc(100% + 32px);
    margin-inline: -16px;
    padding-inline: 3px;
    border-radius: 0px;
  }
`;

export default GridAreas;
